import * as React from 'react'

import { Box, Button, Item } from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsTaxTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  CreateFinanceTaxTypeLedgerAccountAssignmentsInput,
  FinanceValueObjectsTaxTypeEnum,
} from 'generated/resources/graphql'
import { v4 as uuidv4 } from 'uuid'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LedgerAccountAssigmentForm from 'modules/finance/components/CustomCategories/LedgerAccountAssignmentForm'
import { TaxTypeTypeSelector } from 'modules/finance/components/TaxTypeTypeSelector'
import { useLedgerAccountAssignment } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'
import { useTaxTypes } from 'modules/finance/hooks/useTaxTypes'
import { LegalEntity } from 'modules/finance/pages/Accounting/TaxTypePage/types'

import TableWithEditableRows from './TableWithEditableRows'
import EditableTableRow from './TableWithEditableRows/EditableTableRow'
import TaxRateRow, { Row } from './TableWithEditableRows/TaxRateRow'

interface NewTaxTypeFormValues {
  name: string
  taxRates: Row[]
  ledgerAccountId?: number
  type?: FinanceValueObjectsTaxTypeEnum
  country_code?: string
  assignedLedgerAccounts: CreateFinanceTaxTypeLedgerAccountAssignmentsInput[]
}

interface NewTaxTypeModalProps {
  legalEntities: LegalEntity[]
  seePurchaseLedgerAccounts: boolean
  seeSalesLedgerAccounts: boolean
  onClose: () => void
}

const NewTaxTypeModal: React.FC<NewTaxTypeModalProps> = ({
  legalEntities,
  seePurchaseLedgerAccounts,
  seeSalesLedgerAccounts,
  onClose,
}) => {
  const { company } = useCurrentSession()
  const { createTaxType } = useTaxTypes()
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [validateOnChange, setValidateOnChange] = React.useState<boolean>(false)

  const countryList = countries(i18n.locale)

  const form = useForm<NewTaxTypeFormValues>({
    validateOnChange: validateOnChange,
    defaultValues: {
      name: '',
      taxRates: [],
      assignedLedgerAccounts: [],
    },

    validate: (data) => {
      const errors: FormErrors<NewTaxTypeFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('finance.modals.new_tax_type.errors.name_empty')
      }

      if (!data.type || !Object.values(FinanceValueObjectsTaxTypeEnumValues).includes(data.type)) {
        errors.type = i18n.t('finance.modals.new_tax_type.errors.type_empty')
      }

      if (data.taxRates.some((row) => Math.abs(row.value ?? 0) > 100)) {
        errors.taxRates = i18n.t('finance.modals.new_tax_type.errors.amount_validation_v2')
      }

      return errors
    },

    onSubmit: async (values) => {
      const taxRateData = values.taxRates
        .filter((r) => r.value !== null && r.value !== undefined)
        .map((row) => ({
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          rate: row.value! / 100,
          description: row.description,
        }))

      setSubmitting(true)

      const variables = {
        companyId: company.id,
        name: values.name,
        taxRates: taxRateData,
        ledgerAccountId: values.ledgerAccountId,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        type: values.type!,
        countryCode: values.country_code,
        ledgerAccountAssignments: values.assignedLedgerAccounts,
      }

      const result = await createTaxType(variables)

      const handled = handleResult(result, (errors) => {
        // Handle amount errors as a flash if we somehow reach this state
        if (errors.amount) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('finance.modals.new_tax_type.errors.amount_validation'),
          })
        }

        form.setErrors({
          name: errors['name'],
        })
        track(trackingEvents.accounting.addTaxSubmit, {
          status: 'Error',
          name: values.name,
          tax_rate_amounts: taxRateData.map((r) => r.rate).join(', '),
          tax_rate_descriptions: taxRateData.map((r) => r.description).join(', '),
        })
        setSubmitting(false)
      })

      if (handled.success) {
        onClose()

        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.modals.new_tax_type.success'),
        })
        track(trackingEvents.accounting.addTaxSubmit, {
          status: 'Success',
          name: values.name,
          tax_rate_amounts: taxRateData.map((r) => r.rate).join(', '),
          tax_rate_descriptions: taxRateData.map((r) => r.description).join(', '),
        })
      }

      return handled
    },
  })

  const handleClose = () => {
    onClose()
  }

  const handleAddRow = () => {
    const newRow = { id: uuidv4() }
    form.setValues({ ...form.values, taxRates: [...form.values.taxRates, newRow] })
  }

  const handleDeleteRow = (selectedRowId: string) => {
    const newRows = form.values.taxRates.filter((row) => selectedRowId !== row.id)
    form.setValues({ ...form.values, taxRates: newRows })
  }

  const handleRowUpdated = (updatedRow: Row) => {
    const newRows = form.values.taxRates.map((row) => {
      if (row.id === updatedRow.id) {
        return { ...row, value: updatedRow.value, description: updatedRow.description }
      }
      return row
    })
    form.setValues({ ...form.values, taxRates: newRows })
  }

  const onSelectCountry = useUnsafeCallback(
    (item: Item) => {
      form.setValues({ ...form.values, country_code: item.value + '' })
    },
    [form]
  )

  const selectedCountry = useUnsafeMemo(
    () => countryList.find((c) => c.value === form.values.country_code),
    [countryList, form.values.country_code]
  )

  const handleSelectLedgerAccount = useLedgerAccountAssignment(form.setValues)

  useEffect(() => {
    handleAddRow()
    setValidateOnChange(true)
  }, [])

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('finance.modals.new_tax_type.title')}
      description={i18n.t('finance.modals.new_tax_type.description')}
      onClose={handleClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={!form.isValid || submitting} submit>
          {i18n.t('finance.modals.new_tax_type.buttons.add_tax')}
        </Button>
      }
    >
      <Box gap="s24">
        <TextField
          mandatory
          label={i18n.t('finance.modals.new_tax_type.fields.tax_name')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
        />

        <DropdownSearchBar
          name={selectedCountry?.label || ''}
          data={countryList}
          label={i18n.t('finance.modals.new_tax_type.fields.country_selector.label')}
          labelNotFound={''}
          searchLabel={i18n.t('common.search')}
          sortLabel={i18n.t('common.name')}
          placeholder={i18n.t('finance.modals.new_tax_type.fields.country_selector.placeholder')}
          selectLabel={i18n.t('finance.modals.new_tax_type.fields.country_selector.placeholder')}
          autofocus={false}
          onSelect={onSelectCountry}
        />

        <TaxTypeTypeSelector
          onChangeType={(value) => form.setValues({ ...form.values, type: value })}
          error={form.errors.type}
        />

        <TableWithEditableRows
          addRowDisabled={false}
          headers={[
            i18n.t('finance.modals.new_tax_type.fields.value'),
            i18n.t('finance.modals.new_tax_type.fields.description'),
          ]}
          addRowButtonText={i18n.t('finance.modals.new_tax_type.buttons.add_value')}
          emptyStateText={i18n.t('finance.modals.new_tax_type.no_tax_values_added')}
          handleAddRow={handleAddRow}
        >
          {form.values.taxRates.map((row) => (
            <EditableTableRow key={row.id} id={row.id} onDelete={handleDeleteRow}>
              <TaxRateRow row={row} onUpdateRow={handleRowUpdated} />
            </EditableTableRow>
          ))}
        </TableWithEditableRows>

        {/* There is a seeLedgerAccounts that does this in backend
        but as we need the know each of them separately anyway we use them here*/}
        {(seePurchaseLedgerAccounts || seeSalesLedgerAccounts) && (
          <LedgerAccountAssigmentForm
            legalEntities={legalEntities}
            onChange={handleSelectLedgerAccount}
            withOperationType
            seeSalesLedgerAccounts={seeSalesLedgerAccounts}
            seePurchaseLedgerAccounts={seePurchaseLedgerAccounts}
          />
        )}
      </Box>
    </Modal>
  )
}

export default observer(NewTaxTypeModal)
